// src/pages/LoginPage.jsx
import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import axios from "axios";

/**
 * Professional Login Page
 * - Polished gradient background with animated subtle motion
 * - Glassmorphism card, large tap targets, accessible labels
 * - Client-side validation, password visibility toggle
 * - Loading & error feedback with Snackbar
 * - “Remember me” persists token; otherwise session-only
 * - API base URL via Vite env (fallback to localhost)
 */

const API_BASE = import.meta?.env?.VITE_API_URL || "http://localhost:5000";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ username: "", password: "", remember: false });
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [snack, setSnack] = useState({ open: false, severity: "error", message: "" });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // Basic client-side validation
    if (!form.username.trim() || !form.password.trim()) {
      setSnack({ open: true, severity: "error", message: "Username and password are required." });
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/login`, {
        username: form.username.trim(),
        password: form.password,
      });

      const token = data?.token;
      if (!token) {
        throw new Error("Invalid response from server: token missing.");
      }

      // Persist depending on "Remember me"
      if (form.remember) {
        localStorage.setItem("auth_token", token);
      } else {
        sessionStorage.setItem("auth_token", token);
      }

      setSnack({ open: true, severity: "success", message: "Logged in successfully!" });

      // Redirect back to the page the user came from or default to dashboard/home
      const redirectTo = location.state?.from ?? "/dashboard";
      setTimeout(() => navigate(redirectTo, { replace: true }), 300);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Login failed. Please check your credentials.";
      setSnack({ open: true, severity: "error", message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        // Animated gradient backdrop
        background:
          "linear-gradient(135deg, #0f172a, #1e293b, #0b1220, #0f172a, #111827)",
        backgroundSize: "300% 300%",
        animation: "bgmove 18s ease infinite",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "min(440px, 94vw)",
          borderRadius: 4,
          p: { xs: 3, sm: 4 },
          color: "primary.contrastText",
          backdropFilter: "blur(10px)",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 12px 36px rgba(0,0,0,.35)",
        }}
      >
        {/* Title */}
        <Box textAlign="center" mb={1.5}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              letterSpacing: ".3px",
              background:
                "linear-gradient(90deg, #06b6d4 0%, #4f46e5 40%, #1d4ed8 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Welcome back
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
            Please sign in to continue
          </Typography>
        </Box>

        <Divider
          sx={{
            my: 2.5,
            borderColor: "rgba(255,255,255,0.12)",
          }}
        />

        {/* Form */}
        <Box component="form" noValidate onSubmit={handleLogin}>
          <TextField
            label="Username"
            name="username"
            autoComplete="username"
            fullWidth
            required
            margin="normal"
            value={form.username}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineRoundedIcon sx={{ color: "rgba(255,255,255,0.8)" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.06)",
                borderRadius: 2,
              },
              "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.8)" },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.22)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.35)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.55)",
              },
            }}
          />

          <TextField
            label="Password"
            name="password"
            type={showPwd ? "text" : "password"}
            autoComplete="current-password"
            fullWidth
            required
            margin="normal"
            value={form.password}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockRoundedIcon sx={{ color: "rgba(255,255,255,0.8)" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPwd((v) => !v)}
                    edge="end"
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    sx={{ color: "rgba(255,255,255,0.9)" }}
                  >
                    {showPwd ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.06)",
                borderRadius: 2,
              },
              "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.8)" },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.22)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.35)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.55)",
              },
            }}
          />

          <Box
            mt={1}
            mb={1.5}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={form.remember}
                  onChange={handleChange}
                  name="remember"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                  Remember me
                </Typography>
              }
            />

            <Link
              to="/forgot-password"
              style={{ textDecoration: "none" }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#93c5fd",
                  textDecoration: "underline",
                  textUnderlineOffset: "3px",
                  "&:hover": { color: "#bfdbfe" },
                }}
              >
                Forgot password?
              </Typography>
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            size="large"
            disabled={submitting}
            sx={{
              mt: 2,
              py: 1.4,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 800,
              letterSpacing: ".3px",
              color: "#fff",
              background:
                "linear-gradient(90deg, #06b6d4 0%, #4f46e5 50%, #1d4ed8 100%)",
              boxShadow: "0 10px 22px rgba(17,24,39,0.55)",
              "&:hover": {
                filter: "brightness(1.05)",
                boxShadow: "0 12px 30px rgba(17,24,39,0.65)",
                background:
                  "linear-gradient(90deg, #22d3ee 0%, #6366f1 50%, #3b82f6 100%)",
              },
            }}
          >
            {submitting ? (
              <Box display="flex" gap={1} alignItems="center">
                <CircularProgress size={22} sx={{ color: "white" }} />
                Signing in…
              </Box>
            ) : (
              "Login"
            )}
          </Button>
        </Box>

        {/* Divider + Sign up */}
        <Divider
          sx={{
            my: 3,
            borderColor: "rgba(255,255,255,0.12)",
          }}
        />
        <Box textAlign="center">
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
            Don’t have an account?{" "}
            <Link
              to="/register"
              style={{ textDecoration: "none" }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: "#93c5fd",
                  fontWeight: 800,
                  textDecoration: "underline",
                  textUnderlineOffset: "3px",
                  "&:hover": { color: "#bfdbfe" },
                }}
              >
                Sign up
              </Typography>
            </Link>
          </Typography>
        </Box>
      </Paper>

      {/* Animated background keyframes */}
      <style>{`
        @keyframes bgmove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}




















































// import React, { useState } from 'react';
// import { Link } from 'react-router-dom';
// import { TextField, Button, Checkbox, FormControlLabel, Box, Typography, Paper } from '@mui/material';
// import axios from 'axios';

// const LoginPage = () => {
//   const [username, setUsername] = useState('');
//   const [password, setPassword] = useState('');
//   const [error, setError] = useState('');

//   const handleLogin = async (e) => {
//     e.preventDefault();

//     try {
//       const response = await axios.post('http://localhost:5000/login', {
//         username,
//         password,
//       });
//       console.log('Token:', response.data.token); // Handle token storage
//     } catch (err) {
//       setError(err.response?.data?.message || 'An error occurred');
//     }
//   };

//   return (
//     <Box
//       sx={{
//         height: '100vh',
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center',
//         background: 'linear-gradient(135deg, #1e3c72, #2a5298, #6dd5ed)',
//         backgroundSize: '400% 400%',
//         animation: 'gradient 8s ease infinite',
//       }}
//     >
//       <Paper
//         elevation={12}
//         sx={{
//           padding: 5,
//           borderRadius: 5,
//           maxWidth: 400,
//           width: '90%',
//         }}
//       >
//         {/* Logo or Welcome Section */}
//         <Box textAlign="center" mb={3}>
//           <Typography
//             variant="h4"
//             component="h1"
//             fontWeight="bold"
//             color="primary"
//             gutterBottom
//           >
//             Login
//           </Typography>
//           <Typography variant="body1" color="textSecondary">
//             Please enter your credentials to log in
//           </Typography>
//         </Box>

//         {/* Form Section */}
//         <form onSubmit={handleLogin}>
//           <TextField
//             label="Username"
//             variant="outlined"
//             fullWidth
//             margin="normal"
//             required
//             value={username}
//             onChange={(e) => setUsername(e.target.value)}
//           />
//           <TextField
//             label="Password"
//             type="password"
//             variant="outlined"
//             fullWidth
//             margin="normal"
//             required
//             value={password}
//             onChange={(e) => setPassword(e.target.value)}
//           />

//           {error && (
//             <Typography color="error" variant="body2" sx={{ mt: 1 }}>
//               {error}
//             </Typography>
//           )}

//           <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
//             <FormControlLabel control={<Checkbox color="primary" />} label="Remember me" />
//             <Typography
//               variant="body2"
//               color="primary"
//               sx={{ cursor: 'pointer', textDecoration: 'underline' }}
//             >
//               Forgot Password?
//             </Typography>
//           </Box>

//           {/* Login Button */}
//           <Button
//             type="submit"
//             variant="contained"
//             size="large"
//             fullWidth
//             sx={{
//               mt: 3,
//               py: 1.5,
//               textTransform: 'none',
//               fontSize: '1rem',
//               fontWeight: 'bold',
//               background: 'linear-gradient(to right, #1e3c72, #2a5298)',
//               color: '#fff',
//               ':hover': {
//                 background: 'linear-gradient(to right, #2a5298, #6dd5ed)',
//               },
//             }}
//           >
//             Login
//           </Button>
//         </form>

//         {/* Sign Up Section */}
//         <Box textAlign="center" mt={4}>
//           <Typography variant="body2">
//             Don't have an account?{' '}
//             <Link to="/register" style={{ textDecoration: 'none' }}>
//               <Typography
//                 component="span"
//                 variant="body2"
//                 color="primary"
//                 sx={{ fontWeight: 'bold', cursor: 'pointer' }}
//               >
//                 Sign up
//               </Typography>
//             </Link>
//           </Typography>
//         </Box>
//       </Paper>

//       {/* Animation Keyframes */}
//       <style>
//         {`
//           @keyframes gradient {
//             0% { background-position: 0% 50%; }
//             50% { background-position: 100% 50%; }
//             100% { background-position: 0% 50%; }
//           }
//         `}
//       </style>
//     </Box>
//   );
// };

// export default LoginPage;
